import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { fmt } from '../helpers/helpers';
import notesStore from '../stores/NotesStore';
import moment from 'moment';
import ValueHistoryChart from './ValueHistoryChart';
import CrossNoteValueChart from './CrossNoteValueChart';
import './HistoryModal.css';

const HistoryModal = observer(({ entryId, entry: passedEntry, onClose, readOnly = false }) => {
  const store = notesStore;
  // Use passed entry (for shared notes) or find from store
  const entry = passedEntry || store.entries.find(e => e.id === entryId);

  // State for cross-note entries
  const [crossNoteData, setCrossNoteData] = useState(null);
  const [crossNoteLoading, setCrossNoteLoading] = useState(false);
  const [editingPaidAt, setEditingPaidAt] = useState(false);

  // Load cross-note entries when entry has metadataId (skip for readOnly/shared notes)
  const loadCrossNoteEntries = useCallback(async () => {
    if (!entry?.metadataId || readOnly) return;

    setCrossNoteLoading(true);
    try {
      const data = await store.loadEntriesByMetadata(entry.metadataId);
      setCrossNoteData(data);

      // Debug: Log cross-note entries
      if (data?.entries) {
        console.log('[HistoryModal] Cross-note entries:', data.entries);
        console.table(data.entries.map(e => ({
          noteTitle: e.noteTitle,
          noteDate: e.noteDate,
          entryId: e.entryId,
          value: e.value,
          isPaid: e.is_paid
        })));
      }
    } catch (err) {
      console.error('Failed to load cross-note entries:', err);
    } finally {
      setCrossNoteLoading(false);
    }
  }, [entry?.metadataId, store, readOnly]);

  useEffect(() => {
    loadCrossNoteEntries();
  }, [loadCrossNoteEntries]);

  if (!entry) return null;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '–';
    const m = moment(timestamp);
    const now = moment();

    // If today, show time
    if (m.isSame(now, 'day')) {
      return `Hoje, ${m.format('HH:mm')}`;
    }

    // If yesterday
    if (m.isSame(now.subtract(1, 'day'), 'day')) {
      return `Ontem, ${m.format('HH:mm')}`;
    }

    // If this year, don't show year
    if (m.isSame(now, 'year')) {
      return m.format('D/M [às] HH:mm');
    }

    // Full date
    return m.format('D/M/YYYY [às] HH:mm');
  };

  const getEventLabel = (event, isVirtual) => {
    if (isVirtual) {
      return '⏱️ Atual (não salvo)';
    }
    switch (event) {
      case 'created':
        return '🆕 Criado';
      case 'modified':
        return '✏️ Modificado';
      case 'deleted':
        return '🗑️ Removido';
      case 'current':
        return '📍 Atual';
      case 'paid_at_changed':
        return '✅ Data de pagamento';
      case 'payment_source_changed':
        return '💳 Forma de pagamento';
      case 'reminder_only_changed':
        return '🔔 Só lembrete';
      default:
        return '📝 Alterado';
    }
  };

  const getCurrentLabel = () => {
    // Show current entry label (for display purposes)
    if (entry.customName) {
      return entry.customName;
    }
    if (entry.label) {
      return entry.label;
    }
    if (entry.metadataId) {
      // Resolve metadataId to actual name
      const metadataName = store.getMetadataName(entry.metadataId);
      return metadataName || `[ID: ${entry.metadataId.substring(0, 8)}...]`;
    }
    return null;
  };

  const getChangeDescription = (historyItem) => {
    const changes = [];

    if (historyItem.event === 'created') {
      return `Valor: ${fmt(historyItem.value)}${historyItem.label ? `, Descrição: "${historyItem.label}"` : ''}`;
    }

    if (historyItem.event === 'deleted') {
      return 'Entrada removida do texto';
    }

    if (historyItem.event === 'paid_at_changed') {
      const oldLabel = historyItem.oldPaidAt ? formatTimestamp(historyItem.oldPaidAt) : '(vazio)';
      const newLabel = historyItem.paid_at ? formatTimestamp(historyItem.paid_at) : '(vazio)';
      return `Pago em: ${oldLabel} → ${newLabel}`;
    }

    if (historyItem.event === 'payment_source_changed') {
      // Sources are named client-side: the event only stores the id.
      const name = (id) => (id ? store.paymentSourceName(id) : '(nenhuma)');
      return `Forma de pagamento: ${name(historyItem.oldPaymentSourceId)} → ${name(historyItem.paymentSourceId)}`;
    }

    if (historyItem.event === 'reminder_only_changed') {
      const label = (v) => (v ? 'sim' : 'não');
      return `Só lembrete: ${label(historyItem.oldReminderOnly)} → ${label(historyItem.reminderOnly)}`;
    }

    // For current/virtual entry, show current state
    if (historyItem.event === 'current' || historyItem.isVirtual) {
      return `Valor: ${fmt(historyItem.value)}${historyItem.label ? `, Descrição: "${historyItem.label}"` : ''}`;
    }

    // For modifications
    if (historyItem.oldValue !== undefined && historyItem.oldValue !== historyItem.value) {
      changes.push(`Valor: ${fmt(historyItem.oldValue)} → ${fmt(historyItem.value)}`);
    }

    if (historyItem.oldLabel !== undefined && historyItem.oldLabel !== historyItem.label) {
      changes.push(`Descrição: "${historyItem.oldLabel || '(vazio)'}" → "${historyItem.label || '(vazio)'}"`);
    }

    return changes.join(', ') || 'Sem alterações registradas';
  };

  const history = entry.history || [];

  // Add current state as virtual entry if not already in history
  const currentValueInHistory = history.some(h =>
    h.value === entry.value && h.label === entry.label
  );

  let displayHistory = [...history];
  if (!currentValueInHistory && history.length > 0) {
    // Add virtual "current" entry
    displayHistory = [
      ...history,
      {
        timestamp: new Date().toISOString(),
        value: entry.value,
        label: entry.label,
        event: 'current',
        isVirtual: true
      }
    ];
  }

  const sortedHistory = displayHistory.sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            Histórico de Alterações
            {getCurrentLabel() && ` - ${getCurrentLabel()}`}
            {entry.value !== undefined && ` (${fmt(Math.abs(entry.value))})`}
          </h3>
          <button className="close-btn" onClick={onClose} title="Fechar">
            ×
          </button>
        </div>

        <div className="modal-content">
          {/* Entry metadata */}

            {entry.metadataId && (
              <div className="metadata-item metadata-link">
                <span className="metadata-label">Transação frequente:</span>
                <span className="metadata-value">
                  {store.getMetadataName(entry.metadataId) || `ID: ${entry.metadataId.substring(0, 12)}...`}
                  <span style={{ marginLeft: 8, fontSize: 11, color: '#0fb662' }}>
                    ({entry.kind === 'income' ? 'Receita' : 'Despesa'})
                  </span>
                </span>
              </div>
            )}

          <div className="entry-metadata">
            {/* Show current metadata link status */}
            {entry.customName && (
              <div className="metadata-item">
                <span className="metadata-label">📝 Nome personalizado:</span>
                <span className="metadata-value">
                  "{entry.customName}"
                  {entry.kind && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#0fb662' }}>
                      ({entry.kind === 'income' ? '💰 Receita' : '💸 Despesa'})
                    </span>
                  )}
                </span>
              </div>
            )}
            <div className="metadata-item">
              <span className="metadata-label">📅 Criado em:</span>
              <span className="metadata-value">
                {entry.created_at ? formatTimestamp(entry.created_at) : 'Não registrado'}
              </span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">✅ Pago em:</span>
              {!readOnly && editingPaidAt ? (
                <span className="metadata-value">
                  <input
                    type="datetime-local"
                    defaultValue={entry.paid_at ? moment(entry.paid_at).format('YYYY-MM-DDTHH:mm') : ''}
                    autoFocus
                    onBlur={(e) => {
                      const raw = e.target.value;
                      store.changePaidAt(entry, raw ? moment(raw).toISOString() : null);
                      setEditingPaidAt(false);
                    }}
                  />
                  <button
                    className="icon-btn-sm"
                    style={{ marginLeft: 8 }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      store.changePaidAt(entry, null);
                      setEditingPaidAt(false);
                    }}
                  >
                    Limpar
                  </button>
                </span>
              ) : (
                <span
                  className="metadata-value"
                  style={{ cursor: readOnly ? 'default' : 'pointer' }}
                  onClick={() => !readOnly && setEditingPaidAt(true)}
                  title={readOnly ? '' : 'Clique para editar'}
                >
                  {entry.paid_at ? formatTimestamp(entry.paid_at) : 'Não pago'}
                </span>
              )}
            </div>
            <div className="metadata-item">
              <span className="metadata-label">🔄 Última modificação:</span>
              <span className="metadata-value">
                {entry.updated_at ? formatTimestamp(entry.updated_at) : 'Não registrado'}
              </span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">📊 Total de alterações:</span>
              <span className="metadata-value">
                {sortedHistory.filter(h => h.event === 'modified').length}
              </span>
            </div>
          </div>

          {/* Cross-note history section - only show if entry has metadataId and not readOnly */}
          {entry.metadataId && !readOnly && (
            <div className="cross-note-section">
              <h4>🔗 Histórico entre Notas</h4>
              {crossNoteLoading ? (
                <div className="cross-note-loading">Carregando...</div>
              ) : crossNoteData?.entries?.length > 0 ? (
                <>
                  {/* Cross-note chart */}
                  <CrossNoteValueChart entries={crossNoteData.entries} />

                  {/* Cross-note entries list */}
                  <div className="cross-note-list">
                    <table className="cross-note-table">
                      <thead>
                        <tr>
                          <th>Nota</th>
                          <th>Data</th>
                          <th>Valor</th>
                          <th>Status</th>
                          <th className="debug-col">Entry ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crossNoteData.entries.map((crossEntry, idx) => (
                          <tr
                            key={`${crossEntry.noteId}-${crossEntry.entryId}`}
                            className={crossEntry.noteId === store.currentNoteId ? 'current-note' : ''}
                          >
                            <td className="note-title" title={crossEntry.noteTitle}>
                              {crossEntry.noteTitle}
                            </td>
                            <td className="note-date">
                              {moment(crossEntry.noteDate).format('MMM/YYYY')}
                            </td>
                            <td className={`note-value ${crossEntry.value < 0 ? 'expense' : 'income'}`}>
                              {fmt(crossEntry.value)}
                            </td>
                            <td className="note-status">
                              {crossEntry.is_paid ? '✅' : '🕒'}
                            </td>
                            <td className="debug-col" title={`Note: ${crossEntry.noteId}\nEntry: ${crossEntry.entryId}`}>
                              <code>{crossEntry.entryId.substring(0, 8)}</code>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="cross-note-empty">
                  Nenhuma outra ocorrência encontrada
                </div>
              )}
            </div>
          )}

          {/* History list */}
          <div className="history-list">
            <h4>📜 Histórico Completo</h4>
            {sortedHistory.length === 0 ? (
              <div className="no-history">
                Nenhum histórico disponível ainda
              </div>
            ) : (
              <ul className="history-timeline">
                {sortedHistory.map((item, index) => (
                  <li key={index} className={`history-item event-${item.event || 'modified'} ${item.isVirtual ? 'is-virtual' : ''}`}>
                    <div className="history-header">
                      <span className="event-label">{getEventLabel(item.event, item.isVirtual)}</span>
                      <span className="event-time">{formatTimestamp(item.timestamp)}</span>
                    </div>
                    <div className="history-details">
                      {getChangeDescription(item)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Value History Chart */}
          <ValueHistoryChart history={history} />
        </div>

        <div className="modal-footer">
          <button className="done-btn" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
});

export default HistoryModal;
